import { type ReactNode, useCallback, useMemo } from 'react';
import { AuthContext, type AuthContextValue } from '@/hooks/useAuth';
import { authClient } from '@/lib/auth-client';

/**
 * Authentication provider backed by Better Auth (email + password).
 * The admin console is gated to SUPER_ADMIN users in AppLayout.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const { data, isPending, error } = authClient.useSession();
  const sessionUser = data?.user;

  const login = useCallback(async (email: string, password: string) => {
    const { error: signInError } = await authClient.signIn.email({ email, password });
    if (signInError) {
      throw new Error(signInError.message || 'Sign in failed');
    }
  }, []);

  const logout = useCallback(async () => {
    await authClient.signOut();
  }, []);

  const getAccessToken = useCallback(async () => {
    const { data: session } = await authClient.getSession();
    return session?.session?.token ?? '';
  }, []);

  const value: AuthContextValue = useMemo(
    () => ({
      isAuthenticated: !!sessionUser,
      isLoading: isPending,
      user: sessionUser
        ? {
            name: sessionUser.name ?? undefined,
            email: sessionUser.email ?? undefined,
            picture: sessionUser.image ?? undefined,
            sub: sessionUser.id,
          }
        : undefined,
      error: error ? new Error(error.message ?? 'Authentication error') : undefined,
      login,
      logout,
      getAccessToken,
    }),
    [sessionUser, isPending, error, login, logout, getAccessToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
